/**
 * BoletoModal — Ações sobre o boleto de um invoice: download e cancelamento.
 *
 * Cancelar: chama POST /billing/invoices/{id}/cancel. Com boleto o backend dispara
 * CANCEL_BILL ao biller e o invoice só é cancelado quando BOLETO_CANCELED voltar.
 * Download: pendente — o biller ainda não expõe endpoint de download (só fileName no S3).
 */
import { useState } from 'react';
import { toast } from 'react-toastify';
import { FaTimes, FaBarcode, FaDownload, FaBan } from 'react-icons/fa';
import { cancelInvoice } from '../services/billingApi';

interface Props {
    invoice: any;
    onClose: () => void;
    onSuccess: () => void;
}

const BoletoModal: React.FC<Props> = ({ invoice, onClose, onSuccess }) => {
    const [confirming, setConfirming] = useState(false);
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const boleto = invoice.boleto || {};

    const handleCancel = async () => {
        if (!reason.trim()) {
            toast.warn('Informe o motivo do cancelamento');
            return;
        }
        setSubmitting(true);
        try {
            const res = await cancelInvoice(invoice.invoiceId ?? invoice.id, { reason: reason.trim() });
            if (res.result === 'CANCEL_BILL_REQUESTED') {
                toast.info('Cancelamento solicitado ao banco — a fatura será cancelada quando o biller confirmar.');
            } else if (res.result === 'ALREADY_CANCELLED') {
                toast.info('Fatura já estava cancelada.');
            } else {
                toast.success('Fatura cancelada.');
            }
            onSuccess();
            onClose();
        } catch (e: any) {
            toast.error(e.message || 'Erro ao cancelar');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl p-6 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2" title={invoice.invoiceId}>
                        <FaBarcode className="text-blue-500" />
                        Boleto — Fatura {invoice.invoiceId ? invoice.invoiceId.slice(0, 8) : ''}
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                        <FaTimes size={20} />
                    </button>
                </div>

                <div className="mb-4 text-sm space-y-1">
                    <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">Nosso número</span>
                        <span className="font-mono text-gray-900 dark:text-gray-100">{boleto.ourNumber || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">Id do boleto</span>
                        <span className="font-mono text-xs text-gray-900 dark:text-gray-100 break-all text-right max-w-[240px]">{boleto.boletoId || '-'}</span>
                    </div>
                </div>

                <div className="flex flex-col gap-2">
                    <button
                        disabled
                        title="Em breve — o biller ainda não expõe endpoint de download do PDF"
                        className="flex items-center justify-center gap-2 px-4 py-2 text-sm rounded bg-gray-200 text-gray-400 cursor-not-allowed dark:bg-slate-700 dark:text-slate-500"
                    >
                        <FaDownload /> Baixar boleto (em breve)
                    </button>

                    {!confirming ? (
                        <button
                            onClick={() => setConfirming(true)}
                            disabled={invoice.status === 'CANCELLED' || invoice.status === 'PAID'}
                            className="flex items-center justify-center gap-2 px-4 py-2 text-sm rounded bg-red-500 text-white hover:bg-red-600 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
                        >
                            <FaBan /> Cancelar boleto
                        </button>
                    ) : (
                        <div className="border border-red-200 dark:border-red-700 rounded p-3 bg-red-50 dark:bg-red-900/20">
                            <p className="text-xs text-red-700 dark:text-red-300 mb-2">
                                O cancelamento é enviado ao banco e o invoice deixará de ser cobrado. Informe o motivo:
                            </p>
                            <textarea
                                className="w-full text-sm border rounded p-2 mb-2 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                                rows={2}
                                value={reason}
                                onChange={e => setReason(e.target.value)}
                                placeholder="Motivo do cancelamento"
                            />
                            <div className="flex gap-2 justify-end">
                                <button
                                    onClick={() => setConfirming(false)}
                                    className="px-3 py-1.5 text-xs rounded bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-slate-600 dark:text-slate-200"
                                >Voltar</button>
                                <button
                                    onClick={handleCancel}
                                    disabled={submitting}
                                    className="px-3 py-1.5 text-xs rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                                >{submitting ? 'Cancelando...' : 'Confirmar cancelamento'}</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BoletoModal;
